import { useCallback, useEffect, useState } from "react";
import {
  Bell, Plus, Trash2, Play, RefreshCw, Loader2, CheckCircle2, Users,
  History as HistoryIcon,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import "./CommunityIntelligence.css";
import "./AlertsCenter.css";

const TAB = { RULES: "rules", GROUPS: "groups", EVENTS: "events" };

const TRIGGER_LABELS = {
  severe_desert: "Severe resource desert",
  referral_demand_spike: "Referral demand spike",
  resource_capacity_drop: "Resource capacity drop",
  wait_time_breach: "Wait-time breach",
  volunteer_shortage: "Volunteer shortage",
  utilization_spike: "Service utilization spike",
  new_hotspot: "New hotspot emerged",
  community_outcome_decline: "Community outcome decline",
  program_capacity_critical: "Program capacity critical",
};

const RECIPIENT_LABELS = {
  executive_leadership: "Executive Leadership",
  board_members: "Board Members",
  development_team: "Development / Fundraising",
  program_directors: "Program Directors",
  operations_team: "Operations Team",
  community_partners: "Community Partners",
  government_partners: "Government Partners",
  grant_managers: "Grant Managers",
  custom: "Custom",
};

const emptyRule = {
  name: "", description: "", trigger_type: "severe_desert",
  severity: "warning", frequency: "hourly",
  channels: ["in_platform", "email"], enabled: true,
  tenant_scope: "global", partner_id: "",
  escalation_after_hours: 0,
  recipient_group_ids: [], escalation_recipient_group_ids: [],
  config: { threshold: 60 }, geo_scope: {}, program_scope: [],
};

const emptyGroup = {
  name: "", role: "executive_leadership", members: [],
  tenant_scope: "global", partner_id: "",
};

export default function AlertsCenter() {
  const [tab, setTab] = useState(TAB.RULES);
  const [catalog, setCatalog] = useState(null);
  const [rules, setRules] = useState([]);
  const [groups, setGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ruleForm, setRuleForm] = useState(emptyRule);
  const [groupForm, setGroupForm] = useState(emptyGroup);
  const [memberDraft, setMemberDraft] = useState({ email: "", name: "" });
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [c, r, g, e] = await Promise.all([
        api.get("/analytics/alerts/catalog"),
        api.get("/analytics/alerts/rules"),
        api.get("/analytics/alerts/recipient-groups"),
        api.get("/analytics/alerts/events?limit=100"),
      ]);
      setCatalog(c.data);
      setRules(r.data.rules || []);
      setGroups(g.data.groups || []);
      setEvents(e.data.events || []);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  /* ---------------- Rules ---------------- */
  const saveRule = async () => {
    setSaving(true); setError("");
    try {
      const payload = { ...ruleForm };
      // Normalize numeric config values
      if (payload.config) {
        Object.keys(payload.config).forEach((k) => {
          if (payload.config[k] !== "" && !isNaN(Number(payload.config[k])))
            payload.config[k] = Number(payload.config[k]);
        });
      }
      payload.escalation_after_hours = Number(payload.escalation_after_hours || 0);
      if (editingRuleId) {
        await api.patch(`/analytics/alerts/rules/${editingRuleId}`, payload);
      } else {
        await api.post("/analytics/alerts/rules", payload);
      }
      setRuleForm(emptyRule);
      setEditingRuleId(null);
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Delete this alert rule?")) return;
    try { await api.delete(`/analytics/alerts/rules/${id}`); await load(); }
    catch (err) { setError(formatApiError(err)); }
  };

  const evaluateRule = async (id) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/analytics/alerts/rules/${id}/evaluate`);
      setError("");
      alert(`Evaluated: ${data.matched} matched · ${data.dispatched} dispatched`);
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const evaluateAll = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/analytics/alerts/evaluate-now?force_all=true");
      alert(`Cycle complete: ${data.evaluated} evaluated · ${data.matched} matched · ${data.dispatched} dispatched`);
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  /* ---------------- Groups ---------------- */
  const addMember = () => {
    const email = memberDraft.email.trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    setGroupForm({
      ...groupForm,
      members: [...(groupForm.members || []), { ...memberDraft, email }],
    });
    setMemberDraft({ email: "", name: "" });
  };
  const removeMember = (idx) => {
    setGroupForm({
      ...groupForm,
      members: groupForm.members.filter((_, i) => i !== idx),
    });
  };

  const saveGroup = async () => {
    setSaving(true); setError("");
    try {
      if (editingGroupId) {
        await api.patch(`/analytics/alerts/recipient-groups/${editingGroupId}`, groupForm);
      } else {
        await api.post("/analytics/alerts/recipient-groups", groupForm);
      }
      setGroupForm(emptyGroup);
      setEditingGroupId(null);
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };
  const deleteGroup = async (id) => {
    if (!window.confirm("Delete this recipient group?")) return;
    try { await api.delete(`/analytics/alerts/recipient-groups/${id}`); await load(); }
    catch (err) { setError(formatApiError(err)); }
  };

  /* ---------------- Events ---------------- */
  const acknowledge = async (id) => {
    try {
      await api.post(`/analytics/alerts/events/${id}/acknowledge`);
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const startEditRule = (r) => {
    setEditingRuleId(r.id);
    setRuleForm({ ...emptyRule, ...r });
    setTab(TAB.RULES);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const startEditGroup = (g) => {
    setEditingGroupId(g.id);
    setGroupForm({ ...emptyGroup, ...g });
    setTab(TAB.GROUPS);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="hw-ci" data-testid="alerts-center">
      <div className="hw-ci__eyebrow">
        <Bell className="w-3.5 h-3.5"
              style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.3rem" }} />
        Community Intelligence · Alerting Framework
      </div>
      <h1 className="hw-ci__title">Configurable alerts across every trigger</h1>
      <p className="hw-ci__sub">
        Wire community-impact triggers to the people who need to act — with
        thresholds, escalation, and multi-channel delivery.
      </p>

      <div className="hw-alerts__tabs" data-testid="alerts-tabs">
        <button className={`hw-alerts__tab ${tab === TAB.RULES ? "hw-alerts__tab--active" : ""}`}
                onClick={() => setTab(TAB.RULES)}
                data-testid="tab-rules">Rules</button>
        <button className={`hw-alerts__tab ${tab === TAB.GROUPS ? "hw-alerts__tab--active" : ""}`}
                onClick={() => setTab(TAB.GROUPS)}
                data-testid="tab-groups">Recipient groups</button>
        <button className={`hw-alerts__tab ${tab === TAB.EVENTS ? "hw-alerts__tab--active" : ""}`}
                onClick={() => setTab(TAB.EVENTS)}
                data-testid="tab-events">Event history</button>

        <button className="hw-ci__btn hw-ci__btn--primary"
                onClick={evaluateAll}
                disabled={saving}
                style={{ marginLeft: "auto" }}
                data-testid="evaluate-all-btn">
          <Play className="w-3.5 h-3.5" /> Evaluate all now
        </button>
        <button className="hw-ci__btn" onClick={load} data-testid="alerts-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {error && <div style={{ color: "#B91C1C" }} data-testid="alerts-error">{error}</div>}
      {loading && (
        <div style={{ color: "#0F766E" }}>
          <Loader2 className="w-4 h-4 animate-spin"
                    style={{ display: "inline", verticalAlign: "text-bottom" }} /> loading…
        </div>
      )}

      {/* ---------------- RULES TAB ---------------- */}
      {tab === TAB.RULES && catalog && (
        <>
          <div className="hw-alerts__form" data-testid="rule-form">
            <label>
              Name
              <input value={ruleForm.name}
                      onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                      placeholder="e.g. Alert on new Severe Desert"
                      data-testid="rule-name" />
            </label>
            <label>
              Trigger type
              <select value={ruleForm.trigger_type}
                      onChange={(e) => setRuleForm({ ...ruleForm, trigger_type: e.target.value })}
                      data-testid="rule-trigger">
                {catalog.trigger_types.map((t) => (
                  <option key={t} value={t}>{TRIGGER_LABELS[t] || t}</option>
                ))}
              </select>
            </label>
            <label>
              Severity
              <select value={ruleForm.severity}
                      onChange={(e) => setRuleForm({ ...ruleForm, severity: e.target.value })}
                      data-testid="rule-severity">
                {catalog.severities.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>
              Frequency
              <select value={ruleForm.frequency}
                      onChange={(e) => setRuleForm({ ...ruleForm, frequency: e.target.value })}
                      data-testid="rule-frequency">
                {catalog.frequencies.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
            <label>
              Channels
              <select multiple value={ruleForm.channels}
                      onChange={(e) => setRuleForm({
                        ...ruleForm,
                        channels: [...e.target.selectedOptions].map((o) => o.value),
                      })}
                      style={{ minHeight: 80 }}
                      data-testid="rule-channels">
                {catalog.channels.map((c) => (
                  <option key={c} value={c}
                    disabled={!catalog.active_channels.includes(c)}>
                    {c}{catalog.active_channels.includes(c) ? "" : " (coming soon)"}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Tenant scope
              <select value={ruleForm.tenant_scope}
                      onChange={(e) => setRuleForm({ ...ruleForm, tenant_scope: e.target.value })}
                      data-testid="rule-tenant-scope">
                <option value="global">Global (HarborWell staff)</option>
                <option value="partner">Partner-specific</option>
              </select>
            </label>
            {ruleForm.tenant_scope === "partner" && (
              <label>
                Partner ID
                <input value={ruleForm.partner_id || ""}
                        onChange={(e) => setRuleForm({ ...ruleForm, partner_id: e.target.value })}
                        placeholder="partner_organizations.partner_id"
                        data-testid="rule-partner-id" />
              </label>
            )}
            <label>
              Recipient groups
              <select multiple value={ruleForm.recipient_group_ids}
                      onChange={(e) => setRuleForm({
                        ...ruleForm,
                        recipient_group_ids: [...e.target.selectedOptions].map((o) => o.value),
                      })}
                      style={{ minHeight: 80 }}
                      data-testid="rule-recipient-groups">
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} · {RECIPIENT_LABELS[g.role]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Escalation after (hours, 0 = disabled)
              <input type="number" min="0" value={ruleForm.escalation_after_hours}
                      onChange={(e) => setRuleForm({
                        ...ruleForm, escalation_after_hours: e.target.value,
                      })}
                      data-testid="rule-escalation-hours" />
            </label>
            <label style={{ gridColumn: "1 / -1" }}>
              Trigger config (JSON — thresholds, service_type, window_days, etc.)
              <textarea value={JSON.stringify(ruleForm.config || {}, null, 2)}
                        onChange={(e) => {
                          try { setRuleForm({ ...ruleForm, config: JSON.parse(e.target.value) }); }
                          catch { /* keep last valid */ }
                        }}
                        data-testid="rule-config-json" />
            </label>
            <label style={{ gridColumn: "1 / -1", flexDirection: "row",
                              alignItems: "center", gap: "0.5rem" }}>
              <input type="checkbox" checked={ruleForm.enabled}
                      onChange={(e) => setRuleForm({ ...ruleForm, enabled: e.target.checked })}
                      data-testid="rule-enabled" />
              Enabled
            </label>
            <div className="hw-alerts__form-actions">
              {editingRuleId && (
                <button className="hw-ci__btn"
                        onClick={() => { setRuleForm(emptyRule); setEditingRuleId(null); }}
                        data-testid="rule-cancel-edit">Cancel</button>
              )}
              <button className="hw-ci__btn hw-ci__btn--primary"
                      onClick={saveRule}
                      disabled={saving || !ruleForm.name || !ruleForm.trigger_type}
                      data-testid="rule-save">
                <Plus className="w-3.5 h-3.5" />
                {editingRuleId ? "Save changes" : "Create rule"}
              </button>
            </div>
          </div>

          <div className="hw-ci__panel" data-testid="rules-list">
            <h2>
              <span>Alert rules</span>
              <small>{rules.length} configured</small>
            </h2>
            <table className="hw-ci__table">
              <thead>
                <tr>
                  <th>Name</th><th>Trigger</th><th>Severity</th>
                  <th>Freq</th><th>Channels</th><th>Fired</th>
                  <th>Enabled</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} data-testid={`rule-row-${r.id}`}>
                    <td>
                      <strong>{r.name}</strong>
                      {r.description && (
                        <div style={{ fontSize: "0.72rem", color: "#64748B" }}>{r.description}</div>
                      )}
                    </td>
                    <td>{TRIGGER_LABELS[r.trigger_type] || r.trigger_type}</td>
                    <td>
                      <span className={`hw-alerts__severity hw-alerts__severity--${r.severity}`}>
                        {r.severity}
                      </span>
                    </td>
                    <td>{r.frequency}</td>
                    <td>{(r.channels || []).map((c) => (
                      <span key={c} className="hw-alerts__chip">{c}</span>
                    ))}</td>
                    <td>{r.fire_count || 0}</td>
                    <td>{r.enabled ? "✓" : "—"}</td>
                    <td style={{ display: "flex", gap: "0.3rem" }}>
                      <button className="hw-ci__btn" onClick={() => evaluateRule(r.id)}
                              data-testid={`rule-eval-${r.id}`}
                              title="Evaluate now"><Play className="w-3.5 h-3.5" /></button>
                      <button className="hw-ci__btn" onClick={() => startEditRule(r)}
                              data-testid={`rule-edit-${r.id}`}>Edit</button>
                      <button className="hw-ci__btn" onClick={() => deleteRule(r.id)}
                              data-testid={`rule-delete-${r.id}`}
                              title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
                {rules.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: "center",
                                                    padding: "0.9rem",
                                                    color: "#64748B" }}>
                    No rules configured yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ---------------- GROUPS TAB ---------------- */}
      {tab === TAB.GROUPS && catalog && (
        <>
          <div className="hw-alerts__form" data-testid="group-form">
            <label>
              Name
              <input value={groupForm.name}
                      onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                      placeholder="e.g. Board of Directors"
                      data-testid="group-name" />
            </label>
            <label>
              Role
              <select value={groupForm.role}
                      onChange={(e) => setGroupForm({ ...groupForm, role: e.target.value })}
                      data-testid="group-role">
                {catalog.recipient_roles.map((r) => (
                  <option key={r} value={r}>{RECIPIENT_LABELS[r] || r}</option>
                ))}
              </select>
            </label>
            <label>
              Tenant scope
              <select value={groupForm.tenant_scope}
                      onChange={(e) => setGroupForm({ ...groupForm, tenant_scope: e.target.value })}
                      data-testid="group-tenant-scope">
                <option value="global">Global</option>
                <option value="partner">Partner-specific</option>
              </select>
            </label>
            {groupForm.tenant_scope === "partner" && (
              <label>
                Partner ID
                <input value={groupForm.partner_id || ""}
                        onChange={(e) => setGroupForm({ ...groupForm, partner_id: e.target.value })}
                        data-testid="group-partner-id" />
              </label>
            )}
            <div style={{ gridColumn: "1 / -1" }}>
              <label>Members</label>
              <div className="hw-alerts__member-list" data-testid="group-member-list">
                {(groupForm.members || []).length === 0 && (
                  <span style={{ color: "#94A3B8", fontSize: "0.8rem" }}>
                    No members yet
                  </span>
                )}
                {(groupForm.members || []).map((m, i) => (
                  <span key={i} className="hw-alerts__member"
                        data-testid={`group-member-${i}`}>
                    {m.name ? `${m.name} · ` : ""}{m.email}
                    <button onClick={() => removeMember(i)} title="Remove">×</button>
                  </span>
                ))}
              </div>
              <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.4rem" }}>
                <input placeholder="email@org.com"
                        value={memberDraft.email}
                        onChange={(e) => setMemberDraft({ ...memberDraft, email: e.target.value })}
                        data-testid="member-email-input"
                        style={{ flex: 2, padding: "0.45rem 0.6rem",
                                  border: "1px solid #CBD5E1", borderRadius: 8,
                                  fontSize: "0.85rem" }} />
                <input placeholder="Name (optional)"
                        value={memberDraft.name}
                        onChange={(e) => setMemberDraft({ ...memberDraft, name: e.target.value })}
                        data-testid="member-name-input"
                        style={{ flex: 1, padding: "0.45rem 0.6rem",
                                  border: "1px solid #CBD5E1", borderRadius: 8,
                                  fontSize: "0.85rem" }} />
                <button className="hw-ci__btn" onClick={addMember}
                        data-testid="group-add-member">Add</button>
              </div>
            </div>
            <div className="hw-alerts__form-actions">
              {editingGroupId && (
                <button className="hw-ci__btn"
                        onClick={() => { setGroupForm(emptyGroup); setEditingGroupId(null); }}
                        data-testid="group-cancel-edit">Cancel</button>
              )}
              <button className="hw-ci__btn hw-ci__btn--primary"
                      onClick={saveGroup}
                      disabled={saving || !groupForm.name}
                      data-testid="group-save">
                <Users className="w-3.5 h-3.5" />
                {editingGroupId ? "Save group" : "Create group"}
              </button>
            </div>
          </div>

          <div className="hw-ci__panel" data-testid="groups-list">
            <h2>
              <span>Recipient groups</span>
              <small>{groups.length}</small>
            </h2>
            <table className="hw-ci__table">
              <thead>
                <tr>
                  <th>Name</th><th>Role</th><th>Scope</th><th>Members</th><th></th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.id} data-testid={`group-row-${g.id}`}>
                    <td><strong>{g.name}</strong></td>
                    <td>{RECIPIENT_LABELS[g.role] || g.role}</td>
                    <td>{g.tenant_scope}{g.partner_id ? ` · ${g.partner_id.slice(0, 8)}…` : ""}</td>
                    <td>{(g.members || []).length}</td>
                    <td style={{ display: "flex", gap: "0.3rem" }}>
                      <button className="hw-ci__btn" onClick={() => startEditGroup(g)}
                              data-testid={`group-edit-${g.id}`}>Edit</button>
                      <button className="hw-ci__btn" onClick={() => deleteGroup(g.id)}
                              data-testid={`group-delete-${g.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {groups.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: "center", padding: "0.9rem",
                                                   color: "#64748B" }}>
                    No recipient groups yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ---------------- EVENTS TAB ---------------- */}
      {tab === TAB.EVENTS && (
        <div className="hw-ci__panel" data-testid="events-list">
          <h2>
            <span>
              <HistoryIcon className="w-4 h-4"
                            style={{ display: "inline", verticalAlign: "text-bottom",
                                      marginRight: "0.3rem" }} />
              Event history
            </span>
            <small>{events.length} events</small>
          </h2>
          <table className="hw-ci__table">
            <thead>
              <tr>
                <th>Fired</th><th>Rule</th><th>Trigger</th>
                <th>Severity</th><th>Subject</th><th>Channels</th><th></th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} data-testid={`event-row-${e.id}`}>
                  <td style={{ fontSize: "0.75rem", color: "#64748B" }}>
                    {(e.fired_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td><strong>{e.rule_name}</strong></td>
                  <td>{TRIGGER_LABELS[e.trigger_type] || e.trigger_type}</td>
                  <td>
                    <span className={`hw-alerts__severity hw-alerts__severity--${e.severity}`}>
                      {e.severity}
                    </span>
                  </td>
                  <td>{e.subject}</td>
                  <td>
                    {Object.entries(e.delivered_channels || {}).map(([ch, r]) => (
                      <span key={ch} className="hw-alerts__chip">
                        {ch}: {r?.ok ?? 0}✓
                      </span>
                    ))}
                  </td>
                  <td>
                    {e.acknowledged_by ? (
                      <span style={{ color: "#059669", fontSize: "0.8rem" }}>
                        <CheckCircle2 className="w-3.5 h-3.5"
                                        style={{ display: "inline", verticalAlign: "text-bottom" }} /> ACK
                      </span>
                    ) : (
                      <button className="hw-ci__btn"
                              onClick={() => acknowledge(e.id)}
                              data-testid={`event-ack-${e.id}`}>ACK</button>
                    )}
                  </td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: "center",
                                                  padding: "0.9rem", color: "#64748B" }}>
                  No events yet. Create rules and click “Evaluate all now”.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
